/* global initTips */
import * as React from 'react';

import * as PropTypes from 'prop-types';
import { APIRequest } from '@reverbdotcom/discovery-ui';

import { range } from 'lodash';
import PriceFieldWithCurrency from '../shared/price_field_with_currency';
import Loading from '../shared/loading';
import debounce from '../shared/debounce';
import UrlHelpers from '../shared/url_helpers';
import Money from '../shared/money';

const NUDGE_THRESHOLD = 0.75;
const TYPING_DELAY = 800;

class OfferInput extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      approximationLoaded: false,
      showApproximation: false,
    };

    this.onPriceChange = this.onPriceChange.bind(this);
    this.onShippingChange = this.onShippingChange.bind(this);

    if (this.props.disableDebounce) {
      this.onPriceChangeDebounced = this.onPriceChangeDebounced.bind(this);
    } else {
      this.onPriceChangeDebounced = debounce(this.onPriceChangeDebounced.bind(this), TYPING_DELAY);
    }

    this.onQuantityChange = this.onQuantityChange.bind(this);
  }

  onPriceChange(event) {
    const amount = event.target.value;
    if (this.currencyExchangeEnabled()) {
      this.fetchCurrencyEstimateAmount(amount);
    } else if (this.props.onChange) {
      const parsedPrice = Money.parse(amount, this.props.currency);
      const price = {
        amount,
        display: parsedPrice.toLocaleString(
          undefined,
          { minimumFractionDigits: 2, maximumFractionDigits: 2 },
        ),
      };

      this.props.onChange({ price });
    }

    this.onPriceChangeDebounced(event);
  }

  onShippingChange(event) {
    const amount = event.target.value;
    const parsedAmount = Money.parse(amount, this.props.currency);
    const shipping = {
      amount,
      display: parsedAmount.toLocaleString(
        undefined,
        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
      ),
    };

    this.props.onChange({ shipping });
  }

  onPriceChangeDebounced(event) {
    if (this.props.nudgeEnabled) {
      this.setNudge(event.target.value);
    }
  }

  onQuantityChange(event) {
    this.props.onChange({ quantity: event.target.value });
  }

  setNudge(amount) {
    if (!amount) {
      this.setState({ showNudge: false });
      return;
    }

    if (amount / this.props.productPrice < NUDGE_THRESHOLD) {
      this.setState({ showNudge: true });
    } else {
      this.setState({ showNudge: false });
    }
  }

  currencyExchangeEnabled() {
    return this.props.shopCurrency !== this.props.currency;
  }

  fetchCurrencyEstimateAmount(amount) {
    this.setState({
      approximationLoaded: false,
    });

    const url = UrlHelpers.inverseReverseCurrencyExchangePath({
      source: {
        currency: this.props.currency,
        amount,
      },
      target_currency: this.props.shopCurrency,
    });

    const req = APIRequest.V3.get(url);

    req.then((resp) => {
      if (this.props.onChange) {
        this.props.onChange({ price: resp });
      }

      this.setState({
        showApproximation: true,
        approximationLoaded: true,
        price: resp,
      });
    });
  }

  renderTotal() {
    return (
      <PriceFieldWithCurrency
        currency={this.props.currency}
        name="total"
        disabled
        value={this.props.totalPrice}
      />
    );
  }

  renderNudge() {
    if (!this.state.showNudge) { return null; }

    return (
      <div className="alert alert-info">
        <div className="strong">
          <span className="fa fa-exclamation-circle" />
          &nbsp;{I18n.t('js.offerInput.nudgeHeader')}
        </div>
        <div className="small pt-half">{I18n.t('js.offerInput.nudge')}</div>
      </div>
    );
  }

  renderApproximation() {
    if (!this.state.showApproximation) { return null; }
    return (
      <Loading loaded={this.state.approximationLoaded}>
        <div
          ref={(div) => { initTips(div); }}
        >
          <div data-exchange-approximation className="panel strong mb-half">
            <p
              className="mb-0"
              dangerouslySetInnerHTML={{
 __html: I18n.t('js.offerInput.offerApproximation_html', {
 priceDisplay: this.state.price.display, open_tag: '<span className="price-currency">', close_tag: '</span>', priceCurrency: this.state.price.currency, priceSymbol: this.state.price.symbol, shippingPrice: this.props.shippingPrice,
}),
}}
            />
            <p className="normal mb-0 tight-line-height">
              {I18n.t('js.offerInput.valueMayVary', { userCurrency: this.props.currency })}
              <span
                className=" tooltip"
                title={I18n.t('js.offerInput.toolTip_html', { breakTag: '<br>', shopCurrency: this.props.shopCurrency, userCurrency: this.props.currency })}
                data-tooltip="true"
              >
                <span className="fa fa-question-circle" />
              </span>
            </p>
          </div>
        </div>
      </Loading>
    );
  }

  renderQuantityField() {
    if (!this.props.showQuantity) { return null; }

    const maxAvailableToSelect = Math.min(this.props.availableQuantity, 10);

    return (
      <div className="offer-modal__offer-form__qty-and-price__qty">
        <label>
          {I18n.t('js.offerInput.quantity')}
        </label>
        <div className="styled-dropdown">
          <select
            name="offerQuantity"
            id="offer_quantity"
            className="width-100"
            data-quantity="true"
            defaultValue={1}
            onChange={this.onQuantityChange}
          >
            {range(1, maxAvailableToSelect + 1).map(amount =>
              <option key={amount} value={amount}>{amount}</option>)
            }
          </select>
        </div>
      </div>
    );
  }

  renderMultiplicationSign() {
    if (!this.props.showQuantity) { return null; }

    return (
      <div className="offer-modal__offer-form__sign">
        x
      </div>
    );
  }

  renderPriceField() {
    return (
      <PriceFieldWithCurrency
        currency={this.props.currency}
        onChange={this.onPriceChange}
        name={this.props.priceFieldName}
        required="true"
        disabled={this.props.disabledByDefault}
        placeholder="Your Price"
      />
    );
  }

  renderShippingField() {
    if (this.props.shippingEditable) {
      return (
        <PriceFieldWithCurrency
          currency={this.props.currency}
          required="true"
          name={this.props.shippingPriceFieldName}
          disabled={this.props.disabledByDefault}
          onChange={this.onShippingChange}
          value={this.props.shippingPrice}
        />
      );
    }

    return (
      <PriceFieldWithCurrency
        currency={this.props.currency}
        required="true"
        name={this.props.shippingPriceFieldName}
        disabled
        value={this.props.shippingPrice}
      />
    );
  }

  renderInputWithQuantity() {
    return (
      <section>
        <div className="offer-modal__offer-form">
          <div className="offer-modal__offer-form__qty-and-price">
            {this.renderQuantityField()}
            {this.renderMultiplicationSign()}
            <div className="offer-modal__offer-form__qty-and-price__price">
              <label className="pt-1">
                {I18n.t('js.offerInput.bestOffer')}
              </label>
              {this.renderPriceField()}
            </div>
          </div>
          <div className="offer-modal__offer-form__sign offer-modal__offer-form__sign--plus">
            +
          </div>
          <div className="offer-modal__offer-form__shipping">
            <label className="pt-1">
              {I18n.t('js.offerInput.shipping')}
            </label>
            {this.renderShippingField()}
          </div>
          <div className="offer-modal__offer-form__sign offer-modal__offer-form__sign--equals">
            =
          </div>
          <div className="offer-modal__offer-form__total">
            <label className="pt-1">
              {I18n.t('js.offerInput.total')}
            </label>
            {this.renderTotal()}
          </div>
        </div>
        {this.renderNudge()}
        {this.renderApproximation()}
      </section>
    );
  }

  legacyShippingField() {
    if (this.props.shippingEditable) {
      return (
        <PriceFieldWithCurrency
          currency={this.props.currency}
          required="true"
          placeholder={I18n.t('js.offerInput.shipping')}
          name={this.props.shippingPriceFieldName}
          disabled={this.props.disabledByDefault}
        />
      );
    }
    return (
      <div
        className="offer-modal__shipping-locked"
        dangerouslySetInnerHTML={{ __html: `+ ${this.props.shippingSummary}` }}
      />
    );
  }

  renderLegacyInput() {
    return (
      <section name="offer-input">
        <label className="offer-modal__label">{I18n.t('js.offerInput.bestOffer')}</label>
        <div className="g-container">
          <div className="price-field">
            <PriceFieldWithCurrency
              currency={this.props.currency}
              onChange={this.onPriceChange}
              name={this.props.priceFieldName}
              required="true"
              disabled={this.props.disabledByDefault}
            />
          </div>
          <div className="shipping-field">{this.legacyShippingField()}</div>
        </div>
        {this.renderNudge()}
        {this.renderApproximation()}
      </section>
    );
  }

  render() {
    if (this.props.renderInputWithQuantity) {
      return (this.renderInputWithQuantity());
    }

    return (this.renderLegacyInput());
  }
}

OfferInput.defaultProps = {
  priceFieldName: 'price',
  shippingPriceFieldName: 'shipping_price',
  disabledByDefault: false,
  shippingEditable: false,
  nudgeEnabled: false,
  showQuantity: false,
  renderInputWithQuantity: false,
  disableDebounce: false,
};

OfferInput.propTypes = {
  priceFieldName: PropTypes.string,
  shippingPriceFieldName: PropTypes.string,
  shopCurrency: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  disabledByDefault: PropTypes.bool,
  shippingSummary: PropTypes.string.isRequired,
  shippingEditable: PropTypes.bool,
  nudgeEnabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  shippingPrice: PropTypes.string.isRequired,
  productPrice: PropTypes.string.isRequired,
  totalPrice: PropTypes.string.isRequired,
  availableQuantity: PropTypes.number.isRequired,
  showQuantity: PropTypes.bool,
  renderInputWithQuantity: PropTypes.bool,
  disableDebounce: PropTypes.bool,
};

export default OfferInput;



// WEBPACK FOOTER //
// ./javascripts/components/offers/offer_input.jsx